import type { CoverageCell, CoverageGrain } from "@/lib/api";

export type CoverageSource = "all" | "sqm" | "crawl";

export interface CoverageGrid {
  /** Regions, most sales first. */
  regions: { region: string; total: number }[];
  /** Every period from the first to the last with data, gaps included, oldest first. */
  periods: string[];
  /** `${region}|${period}` -> cell. */
  byKey: Map<string, CoverageCell>;
  max: number;
}

export function cellValue(cell: CoverageCell | undefined, source: CoverageSource): number {
  if (!cell) return 0;
  return source === "sqm" ? cell.sqm : source === "crawl" ? cell.crawl : cell.sqm + cell.crawl;
}

function nextPeriod(period: string, grain: CoverageGrain): string {
  if (grain === "year") return String(Number.parseInt(period, 10) + 1);
  const [y, rest] = period.split("-");
  const year = Number.parseInt(y, 10);
  if (grain === "quarter") {
    const quarter = rest.slice(1);
    const q = Number.parseInt(quarter, 10);
    return q === 4 ? `${year + 1}-Q1` : `${year}-Q${q + 1}`;
  }
  const m = Number.parseInt(rest, 10);
  return m === 12 ? `${year + 1}-01` : `${year}-${String(m + 1).padStart(2, "0")}`;
}

/** Periods sort as strings in all three grains (YYYY, YYYY-Qn, YYYY-MM). */
export function buildCoverageGrid(
  cells: CoverageCell[],
  grain: CoverageGrain,
  source: CoverageSource,
): CoverageGrid {
  const byKey = new Map<string, CoverageCell>();
  const totals = new Map<string, number>();
  let first: string | undefined;
  let last: string | undefined;
  let max = 0;
  for (const cell of cells) {
    const v = cellValue(cell, source);
    if (v === 0) continue;
    byKey.set(`${cell.region}|${cell.period}`, cell);
    totals.set(cell.region, (totals.get(cell.region) ?? 0) + v);
    if (!first || cell.period < first) first = cell.period;
    if (!last || cell.period > last) last = cell.period;
    max = Math.max(max, v);
  }
  const periods: string[] = [];
  if (first && last) {
    for (let p = first; p <= last; p = nextPeriod(p, grain)) periods.push(p);
  }
  const regions = [...totals]
    .map(([region, total]) => ({ region, total }))
    .sort((a, b) => b.total - a.total);
  return { regions, periods, byKey, max };
}

/** Shade step 0 (empty) .. 5 on a log scale, so a region with 20 sales still shows. */
export function shadeStep(value: number, max: number): number {
  if (value <= 0 || max <= 0) return 0;
  const step = Math.ceil((Math.log10(value + 1) / Math.log10(max + 1)) * 5);
  return Math.min(5, Math.max(1, step));
}
